// @flow
import _ from "lodash"

import { PAYMENT_ERROR_KEY, PAYMENT_ERROR_TYPES } from "../../consts/enums"

type ZoozError = {
  responseObject: {
    processorError: {
      declineCode: number, // 4002
      declineReason: string, // "Insufficient funds."
    },
    errorDescription: string, // "Card exceeded its limitation. Please use another payment method."
    errorMessage: string, // "Card exceeded its limitation. Please use another payment method."
    responseErrorCode: number, // 393744
  },
  responseStatus: number, // 5
}

type FormattedError = {
  type: string, // $Values<typeof PAYMENT_ERROR_TYPES>
  key?: string, // $Values<typeof PAYMENT_ERROR_KEY>
  msg: string,
}

export const formatZoozConfirmError = (res: ZoozError): FormattedError => {
  const zoozReason = _.get(res, "responseObject.processorError.declineReason")
  let key
  let type
  switch (zoozReason) {
    // User type
    // ===
    case "Card expired.": // It seems your card has expired. Please verify your details or use other payment source.""
    case "TransactionHasBeenDeclined; Expired card": // It seems your card has expired. Please verify your details or use other payment source.""
    case "Expired card.": // Card has expired. Please verify your details or use other payment source.
      type = PAYMENT_ERROR_TYPES.USER
      key = PAYMENT_ERROR_KEY.CARD_EXPIRED
      break

    case "Decline for CVV2 failure.": // "Sorry, cannot process transaction. Your card's CVV number is incorrect."
      type = PAYMENT_ERROR_TYPES.USER
      key = PAYMENT_ERROR_KEY.CARD_CVV_INCORRECT
      break

    case "Authorization declined.": // Card exceeded its limitation. Please use another payment method.
    case "Exceeds cash withdrawal floor limit.": // Transaction declined by the processing bank. Please use another payment source.""
    case "Insufficient funds or credit limit exceeded.": // Card exceeded its limitation. Please use another payment method.""
    case "Exceeds withdrawal count limit.": // "Sorry, but we were unable to process your request at this time. Please try again later or contact the app developer."
    case "TransactionHasBeenDeclined; Exceeds amount limit": // "Cannot complete transaction, you exceeded the amount limitation for a single transaction. Please use other payment source"
    case "REFUSED; Insufficient funds":
      type = PAYMENT_ERROR_TYPES.USER
      key = PAYMENT_ERROR_KEY.CARD_EXCEEDED_LIMITATION
      break

    case "Fraud suspicion.": // Card was declined. We will not be able to process any funds for this card at this moment.""
      type = PAYMENT_ERROR_TYPES.USER
      key = PAYMENT_ERROR_KEY.FRAUD
      break

    case "TransactionHasBeenDeclined; Not sufficient funds": // Card exceeded its limitation. Please use another payment method.""
    case "Insufficient funds.": // Card exceeded its limitation. Please use another payment method.""
      type = PAYMENT_ERROR_TYPES.USER
      key = PAYMENT_ERROR_KEY.CARD_FUNDS
      break

    case "Invalid card data.": // "Sorry, cannot complete transaction. You typed an invalid card number."
    case "Invalid card.": // "Sorry, the card type you entered is not-supported."
    case "Invalid transaction.": // Transaction declined by the processing bank. Please verify that the card details were entered correctly or use another payment source.""
      type = PAYMENT_ERROR_TYPES.USER
      key = PAYMENT_ERROR_KEY.CARD_INVALID
      break

    case "Lost card.": // Card was declined. We will not be able to process any funds for this card at this moment.""
    case "Stolen Card, pick up.": // Card was declined. We will not be able to process any funds for this card at this moment.""
    case "TransactionHasBeenDeclined; Hot Card, Pickup (if possible)": // Card was declined by the processing bank due to excessive use. We will not be able to process any funds for this card at this moment.
      type = PAYMENT_ERROR_TYPES.USER
      key = PAYMENT_ERROR_KEY.CARD_REPORTED
      break

    case "TransactionHasBeenDeclined; Security Violation": // Card was declined. We will not be able to process any funds for this card at this moment.""
      type = PAYMENT_ERROR_TYPES.USER
      key = PAYMENT_ERROR_KEY.CARD_SECURITY
      break

    case "REFUSED; Do not honour": // We were unable to process your request. Please try again later or contact support.
    case "The bank refused the transaction.": // Transaction declined by the processing bank. Please verify that the card details were entered correctly or use another payment source.""
    case "Transaction not permitted to cardholder.": // Transaction declined by the processing bank. Please verify that the card details were entered correctly or use another payment source.""
    case "Transaction processing refused.": // "Sorry, transaction cannot be completed. Please try again or contact the app developer."
    case "TransactionHasBeenDeclined; Restricted card": // Transaction declined by the processing bank. Please use another payment source.""
    case "TransactionHasBeenDeclined; Transaction not permitted on card": // TransactionHasBeenDeclined; Transaction not permitted on card
    case "REFUSED; Transaction not permitted to issuer/cardholder": // We were unable to process your request. Please try again later or contact support.
    case "Transaction not allowed by bank networks to the card holder.": // Transaction declined by the processing bank. Please verify that the card details were entered correctly or use another payment source.""
      type = PAYMENT_ERROR_TYPES.USER
      key = PAYMENT_ERROR_KEY.BANK_REFUSED
      break

    // System
    // ===
    case "Call voice authorization number.": // "Sorry, but we were unable to process your request at this time. Please try again later or contact the app developer."
    case "Exchange protocol failure.": // "Sorry, transaction cannot be completed. Please try again or contact the app developer."
    case "Incorrect PIN.": // "Sorry, cannot complete transaction, please verify you typed the card details correctly."
    case "Invalid amount.": // This transaction is lower than the required minimum. Please change the amount and try again.""
    case "Processing temporarily not possible.": // "Sorry, but we were unable to process your request at this time. Please try again later or contact the app developer."
    case "Terminal ID unknown.": // "Sorry, but we were unable to process your request at this time. Please try again later or contact the app developer."
    case "TransactionHasBeenDeclined; Invalid amount ": // "Sorry, but we were unable to process your request. Please contact the app developer."
    case "TransactionHasBeenDeclined; Invalid transaction card / issuer / acquirer": // "Sorry, but we were unable to process your request. Please contact the app developer."
    case "TransactionHasBeenDeclined; No Credit Account": // "Sorry, but we were unable to process your request. Please contact the app developer."
    case "XML request contains invalid data.": // "Sorry, but we were unable to process your request at this time. Please try again later or contact the app developer."
    case "Restricted card.": // "Sorry, but we were unable to process your request at this time. Please try again later or contact the app developer."
      type = PAYMENT_ERROR_TYPES.SYSTEM
      break

    // Unknown
    // ===
    case "Retain card.": // Transaction declined by the processing bank. Please use another payment source.""
    case "Transaction amount not within pre-defined threshold.": // "Sorry, cannot complete transaction, please verify you typed the card details correctly."
    case "Transaction cannot be completed; violation of law.": // Transaction cannot be completed; violation of law.
    case "Transaction has been denied by the gateway.": // Transaction declined by the processing bank. Please verify that the card details were entered correctly or use another payment source.""
    case "TransactionHasBeenDeclined; Do not Honour": // "The card you're using has already been rejected once. We recommend that you either try another card or wait at least 30 minutes before trying again, to avoid automatic blocking by your card provider."
    default:
      type = PAYMENT_ERROR_TYPES.UNKNOWN
  }

  return {
    type,
    key,
    msg: zoozReason,
  }
}

export const formatZoozAddPaymentMethodError = (errorMessage: string) => {
  let code
  let type
  switch (errorMessage) {
    case "Funds":
      type = PAYMENT_ERROR_TYPES.USER
      code = PAYMENT_ERROR_KEY.CARD_FUNDS
      break

    case "Card exceeded its limitation. Please use another payment method.":
      type = PAYMENT_ERROR_TYPES.USER
      code = PAYMENT_ERROR_KEY.CARD_EXCEEDED_LIMITATION
      break

    case "CVV should have 4 characters.":
      type = PAYMENT_ERROR_TYPES.USER
      code = PAYMENT_ERROR_KEY.CARD_CVV_INCORRECT
      break

    case "Expiration Date is out of range.":
    case "There is a problem processing this credit card. Please re-enter your card number or select another payment option.":
    case "Transaction declined by the processing bank. Please verify that the card details were entered correctly or use another payment source.":
      type = PAYMENT_ERROR_TYPES.USER
      code = PAYMENT_ERROR_KEY.CARD_INVALID
      break

    case "Cannot login, please check the provided credentials.":
    case "Credit card number is not valid. Please use test credit card number on sandbox environment.":
    case "The provided token has expired.":
    case 'Login failed. Unable to read Web Session Token! Please make sure you are using the correct token received by calling "openPayment".':
    case "We\u2019re sorry, but something went wrong. Please contact the app developer for more details.":
    case "Transaction cannot be completed. Please try again or contact support.":
      type = PAYMENT_ERROR_TYPES.SYSTEM
      break

    default:
      type = PAYMENT_ERROR_TYPES.UNKNOWN
  }

  return { type, code, msg: errorMessage }
}
